import { Hub } from "@aws-amplify/core";
import { browserTracingIntegration, init } from "@sentry/react";
import React, { Suspense, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Redirect, Route, BrowserRouter as Router, Switch } from "react-router-dom";
// Self-hosted FontAwesome 7 webfont CSS (replaces the old use.fontawesome.com
// v5.6.3 CDN). Solid-only: the framework + `fa-*` icon classes plus the solid
// @font-face. All webfont icons in the app are solid (`fas`), so brands/regular
// fonts are intentionally not imported. Buttons.css references "Font Awesome 7
// Free" to match the family solid.css defines.
import "@fortawesome/fontawesome-free/css/fontawesome.min.css";
import "@fortawesome/fontawesome-free/css/solid.min.css";
import "src/styles/SearchResults.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-datepicker/dist/react-datepicker.css";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "src/styles/Pickers.css";
import "src/styles/FooterPages.css";
import "src/styles/ExperienceCard.css";
import "src/styles/InteriorPages.css";
import "src/styles/Style.css";
import "src/styles/Buttons.css";
import "src/styles/ExperiencePages.css";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { APIProvider } from "@vis.gl/react-google-maps";
import DelayedLoading from "src/components/DelayedLoading";
import { CustomListRedirect } from "./CustomListRedirect";
import { GOOGLE_API_KEY, STRIPE_TOKEN } from "./cred";
import { PartnerRedirect } from "./PartnerRedirect";

const IconSetup = React.lazy(() => import("./utils/iconSetup"));

if (process.env.NODE_ENV === "production" && window.location.origin === "https://www.scouted.com") {
    init({
        dsn: "https://8d96a8b1998b46bf8dd6e9634337a75d@o510885.ingest.sentry.io/5857747",
        integrations: [browserTracingIntegration()],
        tracesSampleRate: 1.0,
    });
}

const BookAnExperienceHowItWorks = React.lazy(() => import("src/pages/content/BookAnExperienceHowItWorks"));
const BeAScout = React.lazy(() => import("src/pages/content/BeAScout"));
const BeAScoutHowItWorks = React.lazy(() => import("src/pages/content/BeAScoutHowItWorks"));
const BeAScoutPurchaseConfirmation = React.lazy(() => import("src/pages/payments/BeAScoutPurchaseConfirmation"));
const BusinessPurchaseConfirmation = React.lazy(() => import("src/pages/payments/BusinessPurchaseConfirmation"));
const BeAScoutPurchaseOptions = React.lazy(() => import("src/pages/content/BeAScoutPurchaseOptions"));
const BusinessPurchaseOptions = React.lazy(() => import("src/pages/content/BusinessPurchaseOptions"));
const BeAScoutPurchaseFeatures = React.lazy(() => import("src/pages/content/BeAScoutPurchaseFeatures"));
const BusinessPurchaseFeatures = React.lazy(() => import("src/pages/content/BusinessPurchaseFeatures"));
const BeAScoutFaq = React.lazy(() => import("src/pages/content/BeAScoutFaq"));
const BeAScoutAffiliate = React.lazy(() => import("src/pages/content/BeAScoutAffiliate"));
const BusinessPartnershipProgram = React.lazy(() => import("src/pages/content/BusinessPartnershipProgram"));
const BookingLedger = React.lazy(() => import("src/pages/user/BookingLedger"));
const Error = React.lazy(() => import("src/pages/error/Error"));
const NotFound = React.lazy(() => import("src/pages/error/NotFound"));
const ThirdPartyError = React.lazy(() => import("src/pages/error/ThirdPartyError"));
const AboutScouted = React.lazy(() => import("src/pages/content/AboutScouted"));
const BookAnExperienceFaq = React.lazy(() => import("src/pages/content/BookAnExperienceFaq"));
const Receipt = React.lazy(() => import("src/pages/user/Receipt"));
const ScoutReviews = React.lazy(() => import("src/pages/user/ScoutReviews"));
const Coupons = React.lazy(() => import("src/admin/Coupons"));
const Dashboard = React.lazy(() => import("src/admin/Dashboard"));
const Users = React.lazy(() => import("src/admin/Users"));
const CreatePartner = React.lazy(() => import("src/admin/CreatePartner"));
const Organisations = React.lazy(() => import("src/admin/Organisations"));
const OrganisationDetail = React.lazy(() => import("src/admin/OrganisationDetail"));
const PartnerReferralLinks = React.lazy(() => import("src/admin/PartnerReferralLinks"));
const AdminPartnerCustomLists = React.lazy(() => import("src/admin/AdminPartnerCustomLists"));
const PartnerTemplates = React.lazy(() => import("src/admin/PartnerTemplates"));
const AdminPartnerLocations = React.lazy(() => import("src/admin/AdminPartnerLocations"));
const AdminPartnerEmbedWidgets = React.lazy(() => import("src/admin/AdminPartnerEmbedWidgets"));
const PartnerActivityPages = React.lazy(() => import("src/admin/PartnerActivityPages"));
const Experiences = React.lazy(() => import("src/admin/Experiences"));
const Messages = React.lazy(() => import("src/admin/Messages"));
const RefundRequests = React.lazy(() => import("src/admin/RefundRequests"));
const Reviews = React.lazy(() => import("src/admin/Reviews"));
const Affiliates = React.lazy(() => import("src/admin/Affiliates"));
const Configuration = React.lazy(() => import("src/admin/Configuration"));
const AdminCommunicationTemplates = React.lazy(() => import("src/admin/CommunicationTemplates"));
const AdminEmailTracking = React.lazy(() => import("src/admin/EmailTracking"));
const TocAgreement = React.lazy(() => import("src/pages/user/TocAgreement"));
const TocBusAgreement = React.lazy(() => import("src/pages/user/TocBusAgreement"));
const PMAgreement = React.lazy(() => import("src/pages/user/PMAgreement"));
const RebateAgreement = React.lazy(() => import("src/pages/user/RebateAgreement"));
const Calendar = React.lazy(() => import("src/pages/experiences/Calendar"));
const TosAgreement = React.lazy(() => import("./pages/user/TosAgreement"));
const RegistrationProgress = React.lazy(() => import("./pages/user/RegistrationProgress"));
const AffiliateDashboard = React.lazy(() => import("src/pages/user/AffiliateDashboard"));
const ReAuth = React.lazy(() => import("./pages/connect/ReAuth"));
const BackgroundCancellation = React.lazy(() => import("./pages/payments/BackgroundCancellation"));
const MyMessage = React.lazy(() => import("./pages/user/Message"));
const MyMessages = React.lazy(() => import("./pages/user/Messages"));
const ContactUsThanks = React.lazy(() => import("./pages/footer-pages/ContactUsThanks"));
const Login = React.lazy(() => import("./pages/authentication/Login"));
const CreateAccount = React.lazy(() => import("./pages/authentication/CreateAccount"));
const CreateScoutAccount = React.lazy(() => import("./pages/authentication/CreateScoutAccount"));
const Profile = React.lazy(() => import("src/pages/user/Profile"));
const FollowingScouts = React.lazy(() => import("./pages/user/FollowingScouts"));
const ExperienceDetails = React.lazy(() => import("./pages/experiences/ExperienceDetails"));
const EditExperience = React.lazy(() => import("./pages/experiences/EditExperience"));
const ResetVerification = React.lazy(() => import("./pages/authentication/ResetVerification"));
const CreateBusinessAccount = React.lazy(() => import("src/pages/authentication/CreateBusinessAccount"));
const ConfirmSignUp = React.lazy(() => import("./pages/authentication/ConfirmSignUp"));
const ResetPassword = React.lazy(() => import("./pages/authentication/ResetPassword"));
const ChangePassword = React.lazy(() => import("./pages/authentication/ChangePassword"));
const ChangeVerification = React.lazy(() => import("./pages/authentication/ChangeVerification"));
const NewSearch = React.lazy(() => import("./pages/Search"));
const ContactUs = React.lazy(() => import("./pages/footer-pages/ContactUs"));
const Faq = React.lazy(() => import("./pages/footer-pages/Faq"));
const Support = React.lazy(() => import("./pages/footer-pages/Support"));
const WhyTrustScouted = React.lazy(() => import("./pages/footer-pages/WhyTrustScouted"));
const HowScoutedWorks = React.lazy(() => import("./pages/footer-pages/HowScoutedWorks"));
const MakeOnDemandIncome = React.lazy(() => import("./pages/footer-pages/MakeOnDemandIncome"));
const SignUp = React.lazy(() => import("./pages/footer-pages/SignUp"));
const SignUpBoise = React.lazy(() => import("./pages/footer-pages/SignUpBoise"));
const SignUpThanks = React.lazy(() => import("./pages/footer-pages/SignUpThanks"));
const WeAreHiring = React.lazy(() => import("./pages/footer-pages/WeAreHiring"));
const CancellationPolicy = React.lazy(() => import("./pages/footer-pages/CancellationPolicy"));
const Privacy = React.lazy(() => import("./pages/footer-pages/Privacy"));
const PaymentCancelled = React.lazy(() => import("./pages/payments/PaymentCancelled"));
const PaymentSuccess = React.lazy(() => import("./pages/payments/PaymentSuccess"));
const PromoteYourBusiness = React.lazy(() => import("./pages/content/PromoteYourBusiness"));
const PropertyManagerBenefits = React.lazy(() => import("./pages/content/PropertyManagerBenefits"));
const BusinessPaymentCancelled = React.lazy(() => import("./pages/payments/BusinessPaymentCancelled"));
const CreatedExperiences = React.lazy(() => import("./pages/user/CreatedExperiences"));
const BookedExperiences = React.lazy(() => import("./pages/user/BookedExperiences"));
const FavoriteExperiences = React.lazy(() => import("./pages/user/FavoriteExperiences"));
const Bookings = React.lazy(() => import("./pages/user/Bookings"));
const Receipts = React.lazy(() => import("./pages/user/Receipts"));
const Pricing = React.lazy(() => import("./pages/content/Pricing"));
const Maintenance = React.lazy(() => import("src/pages/Maintenance"));
const PartnerActivate = React.lazy(() => import("./pages/authentication/PartnerActivate"));
const AcceptInvitation = React.lazy(() => import("./pages/authentication/AcceptInvitation"));
const PartnerHome = React.lazy(() => import("./partner/Home"));
const ReferralLinks = React.lazy(() => import("./partner/ReferralLinks"));
const Locations = React.lazy(() => import("./partner/Locations"));
const CommunicationTemplates = React.lazy(() => import("./partner/CommunicationTemplates"));
const CustomLists = React.lazy(() => import("./partner/CustomLists"));
const EmbedWidget = React.lazy(() => import("./partner/EmbedWidget"));
const PartnerMembers = React.lazy(() => import("./partner/Members"));
const LandingPage = React.lazy(() => import("./pages/LandingPage"));
const Posts = React.lazy(() => import("src/pages/blog/Posts"));
const Post = React.lazy(() => import("src/pages/blog/Post"));

const MAINTENANCE = false;

const App = () => {
    const [ready, setReady] = useState(false);

    useEffect(() => {
        Hub.listen("datastore", async (hubData) => {
            const { event } = hubData.payload;
            if (event === "ready") {
                setReady(true);
            }
        });
    }, []);

    return MAINTENANCE ? (
        <Router>
            <Switch>
                <Route component={Maintenance} />
            </Switch>
        </Router>
    ) : (
        <Router>
            <Suspense fallback={<DelayedLoading />}>
                <Helmet defaultTitle="Scouted" />
                <IconSetup />
                <Elements stripe={loadStripe(STRIPE_TOKEN)}>
                    <APIProvider apiKey={GOOGLE_API_KEY} libraries={["places"]}>
                        <Switch>
                            <Route path="/admin/dashboard" exact component={Dashboard} />
                            <Route path="/admin/refund-requests" exact component={RefundRequests} />
                            <Route path="/admin/users" exact component={Users} />
                            <Route path="/admin/organisations" exact component={Organisations} />
                            <Route path="/admin/organisations/:id" exact component={OrganisationDetail} />
                            <Route path="/admin/partners/new" exact component={CreatePartner} />
                            <Route path="/admin/partners/edit/:id" exact component={CreatePartner} />
                            <Route
                                path="/admin/partners/:partnerId/referral-links"
                                exact
                                component={PartnerReferralLinks}
                            />
                            <Route
                                path="/admin/partners/:partnerId/custom-lists"
                                exact
                                component={AdminPartnerCustomLists}
                            />
                            <Route path="/admin/partners/:partnerId/templates" exact component={PartnerTemplates} />
                            <Route
                                path="/admin/partners/:partnerId/locations"
                                exact
                                component={AdminPartnerLocations}
                            />
                            <Route
                                path="/admin/partners/:partnerId/embed-widgets"
                                exact
                                component={AdminPartnerEmbedWidgets}
                            />
                            <Route
                                path="/admin/partners/:partnerId/activity-pages"
                                exact
                                component={PartnerActivityPages}
                            />
                            <Route
                                path="/admin/communication-templates"
                                exact
                                component={AdminCommunicationTemplates}
                            />
                            <Route path="/admin/email-tracking" exact component={AdminEmailTracking} />
                            <Route path="/partner/activate" exact component={PartnerActivate} />
                            <Route path="/partner/accept-invitation" exact component={AcceptInvitation} />
                            {/*
                                key={location.key} remounts the page on every navigation, including a
                                click on the sidebar item for the page you're already on — otherwise
                                drill-down state (a selected template, an open builder) would stick
                                around instead of resetting to the page's main view.
                            */}
                            <Route path="/partner/home" exact render={({ location }) => <PartnerHome key={location.key} />} />
                            <Route path="/partner/referral-links" exact render={({ location }) => <ReferralLinks key={location.key} />} />
                            <Route
                                path="/partner/communication-templates"
                                exact
                                render={({ location }) => <CommunicationTemplates key={location.key} />}
                            />
                            <Route path="/partner/locations" exact render={({ location }) => <Locations key={location.key} />} />
                            <Route path="/partner/custom-lists" exact render={({ location }) => <CustomLists key={location.key} />} />
                            <Route path="/partner/embed-widget" exact render={({ location }) => <EmbedWidget key={location.key} />} />
                            <Route path="/partner/members" exact render={({ location }) => <PartnerMembers key={location.key} />} />
                            <Redirect from="/partner" to="/partner/home" exact />
                            <Route path="/reviews/:id" component={ScoutReviews} />
                            <Route path="/bookings-report" exact component={BookingLedger} />
                            <Route path="/admin/experiences" exact component={Experiences} />
                            <Route path="/admin/messages" exact component={Messages} />
                            <Route path="/admin/reviews" exact component={Reviews} />
                            <Route path="/admin/affiliates" exact component={Affiliates} />
                            <Route path="/admin/configuration" exact component={Configuration} />
                            <Route path="/admin/coupons" exact component={Coupons} />
                            <Route exact path="/" component={LandingPage} />
                            <Route path="/blog/:slug" component={Post} />
                            <Route path="/blog" component={Posts} />
                            <Route path="/search" component={NewSearch} />
                            <Route path="/p/:partner/:area" component={PartnerRedirect} />
                            <Route path="/p/:partner" component={PartnerRedirect} />
                            <Route path="/r/:partnerSlug/:listSlug" component={CustomListRedirect} />
                            <Route path="/login" component={Login} />
                            <Route path="/create-business-account" component={CreateBusinessAccount} />
                            <Route path="/create-account" component={CreateAccount} />
                            <Route path="/create-scout-account" component={CreateScoutAccount} />
                            <Route path="/affiliate-dashboard" component={AffiliateDashboard} />
                            <Route path="/confirm-signup" component={ConfirmSignUp} />
                            <Route path="/reset-password" component={ResetPassword} />
                            <Route path="/change-password" component={ChangePassword} />
                            <Route path="/change-password-verification" component={ChangeVerification} />
                            <Route path="/reset-verification" component={ResetVerification} />
                            <Route path="/profile/:id" component={Profile} />
                            <Route path="/following-scouted-local-experts" component={FollowingScouts} />
                            <Route path="/messages" component={MyMessages} />
                            <Route path="/message/:id" component={MyMessage} />
                            <Route path="/experience/:username/:expTitle" exact component={ExperienceDetails} />
                            <Route path="/experience/:state/:city/:expTitle/:id" exact component={ExperienceDetails} />
                            <Route path="/edit/:id" component={EditExperience} />
                            <Route path="/experience/:id/preview" exact component={EditExperience} />
                            <Route path="/booked-experiences" component={BookedExperiences} />
                            <Route path="/bookings" component={Bookings} />
                            <Route path="/receipts" component={Receipts} />
                            <Route path="/receipt/:id" component={Receipt} />
                            <Route path="/apply" component={ContactUs} />
                            <Route path="/registration-progress" component={RegistrationProgress} />
                            <Route path="/created-experiences" component={CreatedExperiences} />
                            <Route path="/payment-cancelled" component={PaymentCancelled} />
                            <Route path="/payment-success" component={PaymentSuccess} />
                            <Route
                                path="/business/subscription-payment-cancelled"
                                component={BusinessPaymentCancelled}
                            />
                            <Route
                                path="/be-a-scout/subscription-payment-cancelled"
                                component={BackgroundCancellation}
                            />
                            <Route path="/privacy" component={Privacy} />
                            <Route path="/terms-and-conditions" component={TocAgreement} />
                            <Route path="/business-listing-terms-and-conditions" component={TocBusAgreement} />
                            <Route path="/cancellation-policy" component={CancellationPolicy} />
                            <Route path="/customer-service" component={ContactUs} />
                            <Route path="/we-are-hiring" component={WeAreHiring} />
                            <Route path="/make-on-demand-income" component={MakeOnDemandIncome} />
                            <Route path="/signup/:id" component={SignUp} />
                            <Route path="/signup" component={SignUp} />
                            <Route path="/signupboise" component={SignUpBoise} />
                            <Route path="/signup-thanks" component={SignUpThanks} />
                            <Route path="/support" component={Support} />
                            <Route path="/faq" component={Faq} />
                            <Route path="/contact-scouted" component={ContactUs} />
                            <Route path="/contact-scouted-thanks" component={ContactUsThanks} />
                            <Route path="/how-scouted-works" component={HowScoutedWorks} />
                            <Route path="/why-trust-scouted" component={WhyTrustScouted} />
                            <Route path="/reauth" component={ReAuth} />
                            <Route path="/tos-agreement" component={TosAgreement} />
                            <Route path="/property-manager-agreement" component={PMAgreement} />
                            <Route path="/rebate-program-agreement" component={RebateAgreement} />
                            <Route path="/calendar" component={Calendar} />
                            <Route path="/favorite-experiences" component={FavoriteExperiences} />
                            <Route path="/error" component={Error} />
                            <Route path="/third-party-error" component={ThirdPartyError} />
                            <Route path="/about/scouted" component={AboutScouted} />
                            <Route path="/book-an-experience/faq" component={BookAnExperienceFaq} />
                            <Route path="/book-an-experience/how-it-works" component={BookAnExperienceHowItWorks} />
                            <Route path="/be-a-scout" component={BeAScout} exact />
                            <Route path="/be-a-scout/faq" component={BeAScoutFaq} />
                            <Route path="/be-a-scout/how-it-works" component={BeAScoutHowItWorks} />
                            <Route path="/be-a-scout/purchase-options" component={BeAScoutPurchaseOptions} />
                            <Route path="/be-a-scout/purchase-confirmation" component={BeAScoutPurchaseConfirmation} />
                            <Route path="/be-a-scout/purchase-features" component={BeAScoutPurchaseFeatures} />
                            <Route path="/be-a-scout/affiliate-program" component={BeAScoutAffiliate} />
                            <Route path="/promote-your-business" component={PromoteYourBusiness} />
                            <Route path="/property-manager-benefits" component={PropertyManagerBenefits} />
                            <Route path="/business/purchase-options" component={BusinessPurchaseOptions} />
                            <Route path="/business/purchase-confirmation" component={BusinessPurchaseConfirmation} />
                            <Route path="/business/purchase-features" component={BusinessPurchaseFeatures} />
                            <Route path="/business/partnership-program" component={BusinessPartnershipProgram} />
                            <Route path="/pricing" component={Pricing} />
                            <Route path="/404" component={NotFound} />
                            <Route path="/:username" exact component={NewSearch} />
                            <Redirect to="/404" />
                        </Switch>
                    </APIProvider>
                </Elements>
            </Suspense>
        </Router>
    );
};

export default App;
